<template>
    <div class="trix-widget">
        <div
            :style="getStyles"
            class="bg-white rounded"
        >
            <div
                v-if="!editMode"
                :style="getStyles"
                v-html="fetchData"
            />
            <RedesignHtmlEditor
                v-else
                :model-value="fetchData"
                @save="onSave"
                @cancel="editMode = false"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WidgetExtended } from '@/types/dashboard'
import { getAllDefaultCardColors } from '@/enum/defaultWidgetSettings'

const RedesignHtmlEditor = defineAsyncComponent(() => import('@/components/RedesignBaseComponents/RedesignHtmlEditor.vue'))

const { t } = useI18n()

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
    }>(),
    {
        editable: false,
        onEditMode: false,
    }
)

const emit = defineEmits<{ (e: 'on-update', data: WidgetExtended): void }>()

const editMode = ref(false)

const fetchData = computed(() => {
    return props.data?.WidgetLayout?.htmlData ?? `<p>${t('widget.clickTheSwitchToEnableEditModeForThisHtml')}</p>`
})

const getStyles = computed(() => {
    const colors = get(props.data?.WidgetLayout, 'colors') ?? getAllDefaultCardColors()
    return {
        border: 0,
        backgroundColor: colors.background,
        color: colors.fonts,
        'margin-top': '10px',
    }
})

function onSave (val: string) {
    if (props.data?.WidgetLayout) {
        props.data.WidgetLayout = {
            ...props.data.WidgetLayout,
            htmlData: val,
        }
    }
    emit('on-update', props.data)
    editMode.value = false
}

onMounted(() => {
    if (props.data?.WidgetLayout && !Object.prototype.hasOwnProperty.call(props.data.WidgetLayout, 'showLastUpdateDate')) {
        props.data.WidgetLayout.showLastUpdateDate = true
    }
})

watch(() => props.editable, (state) => {
    editMode.value = state ?? false
})

watch(() => props.onEditMode, (state) => {
    editMode.value = state ?? false
}, { immediate: true })
</script>

<style lang="scss">
    .trix-widget {
        font-size: unset;
        font-weight: initial;
    }
</style>
